import { useCallback, useEffect, useMemo, useState } from "react";
import { getParentId } from "../utils";

const NOTHING_SELECTED = { selectAll: false, ids: new Set() };

// Rows (parents and annexes) left out by the unticked parents of a "select all"
const countExcludedRows = (ids, docs) => {
    const loadedIds = new Set(docs.map((doc) => doc.id));
    const loadedRows = docs.filter((doc) => ids.has(doc.id) || ids.has(getParentId(doc))).length;
    const unloadedRows = [...ids].filter((id) => !loadedIds.has(id)).length;
    return loadedRows + unloadedRows;
};

// Rows ticked one by one (`ids`), or "select all" over the whole list where `ids` are the
// unticked rows. Search hits are parents only, so under "select all" annexes follow their parent.
// `count` is in rows (parents and annexes); `parentCount` in parents, what filters apply to.
const useDocsSelection = ({ docs = [], totalDocs = 0, totalDocsWithAnnexes = 0, searchFilters }) => {
    const [selection, setSelection] = useState(NOTHING_SELECTED);
    const { selectAll, ids } = selection;

    // A "select all" belongs to the list it was made on
    useEffect(() => {
        setSelection((prev) => (prev.selectAll ? NOTHING_SELECTED : prev));
    }, [searchFilters]);

    const isSelected = useCallback(
        (doc) => (selectAll ? !ids.has(getParentId(doc) || doc.id) : ids.has(doc.id)),
        [selectAll, ids]
    );

    const isLocked = useCallback((doc) => selectAll && !!getParentId(doc), [selectAll]);

    const toggleRow = useCallback((doc) => {
        setSelection((prev) => {
            const next = new Set(prev.ids);
            if (next.has(doc.id)) next.delete(doc.id);
            else next.add(doc.id);
            // Unticking every row of a "select all" leaves nothing selected
            if (prev.selectAll && next.size >= totalDocs) return NOTHING_SELECTED;
            return { ...prev, ids: next };
        });
    }, [totalDocs]);

    const isAllSelected = selectAll && ids.size === 0 && totalDocs > 0;

    const toggleAll = useCallback(() => {
        setSelection(isAllSelected ? NOTHING_SELECTED : { selectAll: true, ids: new Set() });
    }, [isAllSelected]);

    const clear = useCallback(() => setSelection(NOTHING_SELECTED), []);

    const count = selectAll ? Math.max(totalDocsWithAnnexes - countExcludedRows(ids, docs), 0) : ids.size;
    const parentCount = selectAll ? Math.max(totalDocs - ids.size, 0) : undefined;

    const selectedDocs = useMemo(() => docs.filter(isSelected), [docs, isSelected]);

    // Body for the bulk endpoints; `pickedDocs` lets a caller narrow picked rows (e.g. drop annexes)
    const buildRequest = useCallback((pickedDocs = selectedDocs) => (
        selectAll
            ? { selectAll: true, filters: searchFilters, excludeIds: [...ids], expectedCount: totalDocs }
            : { docIds: pickedDocs.map((doc) => doc.id) }
    ), [selectAll, ids, searchFilters, totalDocs, selectedDocs]);

    return { isSelectAll: selectAll, isAllSelected, count, parentCount, selectedDocs, isSelected, isLocked, toggleRow, toggleAll, clear, buildRequest };
};

export default useDocsSelection;
